/**
 * "Compare with live", in words.
 *
 * The server sends one line per job (and seat) whose allowed models differ
 * between the live policy and the one you are looking at. "ADDED gemma4" told
 * you nothing about which side had it, or what the job does without it. Each
 * line here says it plainly: what live allows, what this one allows, and what
 * that means for the job.
 */
import type { PipelineDiffRow } from '../types';

const split = (s: string | null): string[] => (s ? s.split(',').map((x) => x.trim()).filter(Boolean) : []);

export interface DiffLine {
  key: string;
  job: string;
  live: string[];
  here: string[];
  /** Models this policy allows that live does not. */
  gained: string[];
  /** Models live allows that this policy does not. */
  lost: string[];
  sentence: string;
}

export function diffLines(
  rows: PipelineDiffRow[],
  { label, here }: { label: (stage: string) => string; here: string },
): DiffLine[] {
  return rows.map((d) => {
    const live = split(d.before);
    const mine = split(d.after);
    const job = `${label(d.stage)}${d.archetype ? ` · ${d.archetype}` : ''}`;
    const sentence =
      d.change === 'added'
        ? `Live has no rule of its own for this job, so it follows its group or DEFAULT. ${here} gives it its own models.`
        : d.change === 'removed'
          ? `${here} drops this job's own rule, so it falls back to its group or DEFAULT.`
          : `Both have a rule for this job, with different models.`;
    return {
      key: `${d.stage}:${d.archetype ?? ''}`,
      job,
      live,
      here: mine,
      gained: mine.filter((m) => !live.includes(m)),
      lost: live.filter((m) => !mine.includes(m)),
      sentence,
    };
  });
}
